import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import VarietyModal from '../components/VarietyModal';
import CategoryFilterBar from '../components/CategoryFilterBar';
import { getProducts } from '../api';
import { normalizePaginatedResponse } from '../utils/pagination';
import useProductEvents from '../hooks/useProductEvents';
import { applyProductEvent } from '../utils/productEventPatch';

const STATIC_CATEGORIES = [
  { id: 'chama-fruit', label: 'Frutas y Verduras' },
  { id: 'chama-max', label: 'Construcción' },
  { id: 'chama-todo', label: 'Otros Alimentos' },
];

const ProductGallery = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');

  const buildFilteredProducts = (productsList, search, category) => {
    let filtered = productsList;
    if (search) {
      filtered = productsList.filter(product =>
        product.productName.toLowerCase().split(' ').some(word => word.startsWith(search.trim().toLowerCase()))
      );
    } else if (category !== 'all') {
      filtered = productsList.filter((product) => product.category === category);
    }

    filtered = filtered.filter(product =>
      product.Varieties?.some(variety =>
        (variety.VarietyPrices || []).some(
          (price) => price.price !== null && price.price !== undefined && !price.isSoldOut
        )
      )
    );

    return filtered;
  };

  const refreshProducts = useCallback(async () => {
    try {
      const data = await getProducts(branchId || 1, { pageSize: 0 });
      const normalized = normalizePaginatedResponse(data);
      const productsData = normalized.data || [];
      setProducts(productsData);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  }, [branchId]);

  const handleProductEvent = useCallback((event) => {
    const nextProducts = applyProductEvent(products, event);
    if (!nextProducts) {
      refreshProducts();
      return;
    }
    if (nextProducts === products) return;
    setProducts(nextProducts);
  }, [products, refreshProducts]);

  useProductEvents(branchId, handleProductEvent);

  useEffect(() => {
    refreshProducts();
  }, [refreshProducts]);

  useEffect(() => {
    setFilteredProducts(buildFilteredProducts(products, searchTerm, selectedCategory));
  }, [products, searchTerm, selectedCategory]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    filterProducts(value, selectedCategory);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    filterProducts(searchTerm, category);
  };

  const filterProducts = (search, category) => {
    setFilteredProducts(buildFilteredProducts(products, search, category));
  };

  return (
    <>
      <div className="p-6 bg-gray-100 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-center mb-6">Lista de Productos</h1>

          {/* Search Bar */}
          <div className="sticky top-14 pt-2 z-10 bg-gray-100">
            <input
              type="text"
              placeholder="Buscar producto..."
              value={searchTerm}
              onChange={(e) => {
                handleSearch(e);
              }}
              className="w-full mb-6 p-3 border border-gray-300 rounded-lg shadow focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <CategoryFilterBar
            categories={STATIC_CATEGORIES}
            selectedCategory={selectedCategory}
            onSelect={handleCategorySelect}
            includeAll
            allItem={{ id: 'all', label: 'Todos', initial: 'T' }}
            itemsWrapperClassName="mb-6"
          />

          {/* Product Grid */}
          <div className="space-y-8">
            {(selectedCategory === 'all' || searchTerm ? ["chama-fruit", "chama-todo"] : [selectedCategory]).map((category) => {
              const productsInCategory = filteredProducts
                .filter((p) => p.category === category)
                .sort((a, b) => a.productName.localeCompare(b.productName)); // sort alphabetically

              if (productsInCategory.length === 0) return null;

              return (
                <div key={category}>
                  {/* Category Title */}
                  <div className="flex items-center gap-4 my-6">
                    <div className="flex-grow border-t border-gray-300"></div>
                    <h2 className="text-lg font-bold text-gray-700 whitespace-nowrap">
                      {category === 'chama-fruit'
                        ? 'Frutas y Verduras'
                        : category === "chama-max" ? "Construcción" : 'Otros Alimentos'}
                    </h2>
                    <div className="flex-grow border-t border-gray-300"></div>
                  </div>

                  {/* Products Grid */}
                  <div className="grid grid-cols-3 gap-1 md:grid-cols-4 lg:grid-cols-6">
                    {productsInCategory.map((product) => (
                      <div
                        key={product.id}
                        className="p-4 bg-white rounded-lg shadow hover:shadow-lg transition-shadow flex flex-col justify-between cursor-pointer"
                        onClick={() => setSelectedProduct(product)}
                      >
                        <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                          <img
                            src={product.productImage || 'https://placehold.co/200'}
                            alt={product.productName}
                            className="absolute inset-0 w-full h-full object-cover rounded"
                          />
                        </div>
                        <h3 className="text-sm font-semibold break-words mt-2">
                          {product.productName} <br />[ver detalle]
                        </h3>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal for Varieties */}
      {selectedProduct && (
        <VarietyModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </>
  );
};

export default ProductGallery;
